import React, { useEffect, useMemo, useState } from 'react';
import MangaReviewModeration from './MangaReviewModeration';
import '../../App.css';
import './AdminPage.css';
import '../manga/MangaPage.css';
import MangaImageLightbox from '../../components/manga/MangaImageLightbox';
import MangaApiService from '../../services/MangaApiService';
import ExternalLinkService from '../../services/ExternalLinkService';
import MangaAccessNotice from '../manga/MangaAccessNotice';
import { MANGA_INACTIVITY_DAYS, getMangaErrorMessage } from '../manga/mangaStudioUtils';
import chaptersIcon from '../../assets/ic_collection.svg';
import mangaIcon from '../../assets/ic_category.svg';
import teamIcon from '../../assets/ic_profile.svg';
import placeholderCover from '../../assets/card_placeholder.png';
import AdminLayout from './AdminLayout';

const CHAPTERS_PAGE_SIZE = 40;
const ORDER_OPTIONS = [
    { value: 'new', label: 'Новые сверху' },
    { value: 'old', label: 'Старые сверху' },
];

const normalizeStatus = (status) => String(status || '').trim().toLowerCase();
const isPendingStatus = (status) => normalizeStatus(status) === 'pending';
const isRejectedStatus = (status) => normalizeStatus(status) === 'rejected';
const isDeletedStatus = (status) => ['deleted', 'soft_deleted', 'soft-deleted', 'removed'].includes(normalizeStatus(status));

const translateStatus = (status) => {
    const normalized = normalizeStatus(status);
    if (!normalized) {
        return 'Неизвестно';
    }
    switch (normalized) {
        case 'pending':
            return 'На модерации';
        case 'approved':
            return 'Одобрено';
        case 'published':
            return 'Опубликовано';
        case 'rejected':
            return 'Отклонено';
        case 'draft':
            return 'Черновик';
        case 'deleted':
        case 'soft_deleted':
        case 'soft-deleted':
        case 'removed':
            return 'Удалено';
        case 'active':
            return 'Активно';
        case 'inactive':
            return 'Отключено';
        default:
            return status;
    }
};

const getStatusClass = (status) => {
    const normalized = normalizeStatus(status);
    if (!normalized) {
        return 'unknown';
    }
    if (normalized === 'pending') {
        return 'pending';
    }
    if (['approved', 'published', 'active'].includes(normalized)) {
        return 'approved';
    }
    if (['rejected', 'inactive', 'deleted', 'soft_deleted', 'soft-deleted', 'removed'].includes(normalized)) {
        return 'rejected';
    }
    if (normalized === 'draft') {
        return 'draft';
    }
    return 'unknown';
};

const getTeamId = (team) => team?.id || team?.teamId || team?.team_id;
const getTeamName = (team, fallbackId) => team?.name || team?.title || team?.teamName || (fallbackId ? `Команда ${fallbackId}` : 'Команда');
const getTeamStatus = (team) => team?.status || team?.state || team?.moderationStatus;
const getChapterId = (chapter) => chapter?.id || chapter?.chapterId || chapter?.chapter_id;
const getChapterStatus = (chapter) => chapter?.status || chapter?.state || chapter?.moderationStatus;
const getChapterNumber = (chapter) => chapter?.number ?? chapter?.chapterNumber ?? chapter?.index ?? chapter?.chapter_index;
const getChapterTitle = (chapter) => chapter?.title || chapter?.name || '';
const getChapterMangaTitle = (chapter) => chapter?.mangaTitle || chapter?.manga?.title || '';
const getChapterMangaSlug = (chapter) => chapter?.mangaSlug || chapter?.manga?.slug || '';
const getChapterMangaId = (chapter) => chapter?.mangaId || chapter?.manga_id || chapter?.manga?.id;
const getChapterMangaStatus = (chapter) => chapter?.mangaStatus || chapter?.manga_status || chapter?.manga?.status;
const getChapterTeamId = (chapter) => chapter?.teamId || chapter?.team_id || chapter?.team?.id;
const getChapterCoverMeta = (chapter) => ({
    coverUrl: chapter?.mangaCoverUrl,
    coverFileId: chapter?.mangaCoverFileId,
    coverStorageBucket: chapter?.mangaCoverStorageBucket,
});
const getChapterCoverUrl = (chapter) => MangaApiService.resolveCoverUrl(getChapterCoverMeta(chapter));
const getMangaId = (manga) => manga?.id || manga?.mangaId || manga?.manga_id;
const getMangaTeamId = (manga) => (
    manga?.teamId
    || manga?.team_id
    || manga?.team?.id
    || manga?.team?.teamId
    || manga?.team?.team_id
);
const getMangaTeamName = (manga) => (
    manga?.teamName
    || manga?.team_name
    || manga?.team?.name
    || manga?.teamTitle
    || manga?.team?.title
);
const getMangaTitle = (manga) => manga?.title || manga?.name || manga?.slug || 'Без названия';
const getMangaStatus = (manga) => manga?.status || manga?.state || manga?.moderationStatus;
const getMangaPublisher = (manga) => (
    manga?.publisher
    || manga?.publisherName
    || manga?.publisher_name
    || null
);
const getMangaAuthor = (manga) => (
    manga?.author
    || manga?.writer
    || manga?.artist
    || manga?.creator
    || manga?.authorName
    || manga?.creatorName
    || null
);
const normalizeGenres = (genres) => {
    if (!genres) {
        return [];
    }
    if (Array.isArray(genres)) {
        return genres
            .map((item) => String(item ?? '').trim())
            .filter(Boolean);
    }
    if (typeof genres === 'string') {
        return genres
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean);
    }
    return [];
};
const formatGenres = (genres) => {
    const list = normalizeGenres(genres);
    return list.length ? list.join(', ') : '';
};
const formatFieldValue = (value) => (value === null || value === undefined || value === '' ? '—' : value);
const getMangaNsfwValue = (manga) => {
    if (!manga) {
        return null;
    }
    const candidates = [manga.nsfw, manga.isNsfw, manga.is_nsfw];
    for (const candidate of candidates) {
        if (candidate !== undefined && candidate !== null) {
            return candidate;
        }
    }
    return null;
};
const formatNsfwLabel = (manga) => {
    const value = getMangaNsfwValue(manga);
    if (value === null) {
        return '—';
    }
    return value ? 'да' : 'нет';
};
const getRejectionReason = (item) => (
    item?.rejectionReason
    || item?.rejection_reason
    || item?.rejectReason
    || item?.reject_reason
    || item?.moderationReason
    || item?.moderation_reason
    || item?.reason
    || null
);
const parseAuditDetails = (details) => {
    if (!details) {
        return null;
    }
    if (typeof details === 'object') {
        return details;
    }
    if (typeof details === 'string') {
        try {
            return JSON.parse(details);
        } catch (error) {
            return { raw: details };
        }
    }
    return null;
};
const pickDetailValue = (details, keys) => {
    if (!details) {
        return null;
    }
    for (const key of keys) {
        const value = details[key];
        if (value !== undefined && value !== null && value !== '') {
            return value;
        }
    }
    return null;
};
const parseIdFromText = (text, pattern) => {
    if (!text) {
        return null;
    }
    const match = text.match(pattern);
    return match ? match[1] : null;
};
const extractAuditTargets = (entry) => {
    const action = String(entry?.action || '').toLowerCase();
    const details = parseAuditDetails(entry?.details);
    const rawDetails = typeof entry?.details === 'string' ? entry.details : '';
    let mangaId = pickDetailValue(details, ['mangaId', 'manga_id', 'mangaID', 'manga']);
    if (!mangaId && details?.manga?.id) {
        mangaId = details.manga.id;
    }
    let chapterId = pickDetailValue(details, ['chapterId', 'chapter_id', 'chapterID', 'chapter']);
    if (!chapterId && details?.chapter?.id) {
        chapterId = details.chapter.id;
    }
    const targetType = String(
        details?.targetType
        || details?.entityType
        || details?.type
        || details?.entity
        || ''
    ).toLowerCase();
    if (!mangaId && targetType.includes('manga')) {
        mangaId = pickDetailValue(details, ['targetId', 'entityId', 'id']);
    }
    if (!chapterId && targetType.includes('chapter')) {
        chapterId = pickDetailValue(details, ['targetId', 'entityId', 'id']);
    }
    if (!mangaId) {
        mangaId = parseIdFromText(rawDetails, /manga(?:Id|_id)?\s*[:=]\s*([\w-]+)/i);
    }
    if (!chapterId) {
        chapterId = parseIdFromText(rawDetails, /chapter(?:Id|_id)?\s*[:=]\s*([\w-]+)/i);
    }
    if (!mangaId && action.includes('manga') && details?.id) {
        mangaId = details.id;
    }
    if (!chapterId && action.includes('chapter') && details?.id) {
        chapterId = details.id;
    }
    return { mangaId, chapterId };
};
const isRollbackableAudit = (entry) => {
    const action = String(entry?.action || '').toLowerCase();
    if (!action) {
        return false;
    }
    return action.includes('delete')
        || action.includes('remove')
        || action.includes('soft')
        || action.includes('purge')
        || action.includes('archive');
};
const isDeletedEntry = (item, status) => Boolean(
    isDeletedStatus(status)
    || item?.deletedAt
    || item?.deleted_at
    || item?.deletedOn
    || item?.deleted_on
    || item?.isDeleted
    || item?.deleted
);
const resolveDonationLinks = (team) => {
    if (!team) {
        return [];
    }
    const raw = team.donationLinks || team.donateLinks || team.donations || [];
    if (Array.isArray(raw)) {
        return raw.filter(Boolean);
    }
    return raw ? [raw] : [];
};
const getTeamLogoMeta = (team) => {
    if (!team) {
        return { fileId: null, bucket: null };
    }
    const logo = team.logo || team.logoFile || team.logo_data || {};
    return {
        fileId: team.logoFileId || team.logo_file_id || logo.fileId || logo.id || null,
        bucket: team.logoStorageBucket || team.logo_storage_bucket || logo.bucket || logo.storageBucket || logo.storage_bucket || null,
    };
};

const normalizeChapterPages = (pages) => {
    if (!pages) {
        return [];
    }
    if (Array.isArray(pages)) {
        return pages;
    }
    if (Array.isArray(pages?.items)) {
        return pages.items;
    }
    if (Array.isArray(pages?.content)) {
        return pages.content;
    }
    if (Array.isArray(pages?.data)) {
        return pages.data;
    }
    return [];
};

const unwrapPagesResponse = (response) => {
    if (!response) {
        return [];
    }
    const payload = response?.data || response;
    if (Array.isArray(payload)) {
        return payload;
    }
    if (Array.isArray(payload?.data)) {
        return payload.data;
    }
    if (Array.isArray(payload?.content)) {
        return payload.content;
    }
    if (Array.isArray(payload?.items)) {
        return payload.items;
    }
    return normalizeChapterPages(payload);
};

const getPageNumber = (page, index) => {
    if (!page) {
        return index + 1;
    }
    const value = page.pageNumber || page.number || page.page || page.index;
    return value || index + 1;
};

const getPageFileId = (page) => page?.fileId || page?.id || page?.file_id;
const getPageBucket = (page, fallback) => (
    page?.storageBucket
    || page?.storage_bucket
    || page?.bucket
    || fallback
);

const unwrapList = (response) => {
    if (!response) {
        return [];
    }
    const candidates = [
        response,
        response?.data,
        response?.data?.data,
        response?.data?.data?.data,
    ];
    for (const candidate of candidates) {
        if (!candidate) {
            continue;
        }
        if (Array.isArray(candidate)) {
            return candidate;
        }
        if (Array.isArray(candidate.items)) {
            return candidate.items;
        }
        if (Array.isArray(candidate.content)) {
            return candidate.content;
        }
    }
    return [];
};

const unwrapModerationPage = (response) => {
    if (!response) {
        return { items: [], last: true, page: 0 };
    }
    const payload = response?.data || response;
    if (Array.isArray(payload)) {
        return { items: payload, last: true, page: 0 };
    }
    const content = Array.isArray(payload?.content)
        ? payload.content
        : (Array.isArray(payload?.items) ? payload.items : []);
    const page = Number(payload?.number ?? payload?.page ?? 0);
    const totalPages = Number(payload?.totalPages ?? payload?.total_pages ?? 0);
    const last = payload?.last ?? (totalPages ? page >= totalPages - 1 : content.length === 0);
    return { items: content, last, page };
};

const getCreatedAtValue = (item) => (
    item?.createdAt
    || item?.created_at
    || item?.createdOn
    || item?.created_on
    || item?.createdDate
    || item?.created_date
    || null
);
const getCreatedAtMs = (item) => {
    const value = getCreatedAtValue(item);
    if (!value) {
        return null;
    }
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
};
const sortByCreatedAt = (items, order) => {
    const direction = order === 'old' ? 1 : -1;
    return [...items].sort((left, right) => {
        const leftTime = getCreatedAtMs(left);
        const rightTime = getCreatedAtMs(right);
        if (leftTime === null && rightTime === null) {
            return 0;
        }
        if (leftTime === null) {
            return 1;
        }
        if (rightTime === null) {
            return -1;
        }
        return direction * (leftTime - rightTime);
    });
};

const isSameId = (left, right) => String(left ?? '') === String(right ?? '');

const mangaTeamInFlight = new Map();

const matchesTeamQuery = (team, query) => {
    if (!query) {
        return true;
    }
    const teamId = getTeamId(team) || team?.id;
    const name = getTeamName(team, teamId) || '';
    const queryValue = query.toLowerCase();
    return name.toLowerCase().includes(queryValue) || String(teamId || '').toLowerCase().includes(queryValue);
};

const MangaModerationPage = () => {
    const [activeTab, setActiveTab] = useState('chapters');
    const [searchQuery, setSearchQuery] = useState('');
    const [chapters, setChapters] = useState([]);
    const [selectedChapter, setSelectedChapter] = useState(null);
    const [chapterPage, setChapterPage] = useState(0);
    const [chapterHasMore, setChapterHasMore] = useState(true);
    const [pendingOnly, setPendingOnly] = useState(true);
    const [chapterOrder, setChapterOrder] = useState('new');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [actionMessage, setActionMessage] = useState(null);
    const [rejectReasons, setRejectReasons] = useState({});
    const [mangaQuery, setMangaQuery] = useState('');
    const [mangaModerationList, setMangaModerationList] = useState([]);
    const [selectedModerationManga, setSelectedModerationManga] = useState(null);
    const [mangaPendingOnly, setMangaPendingOnly] = useState(true);
    const [mangaInactiveOnly, setMangaInactiveOnly] = useState(false);
    const [mangaOrder, setMangaOrder] = useState('new');
    const [mangaModerationLoading, setMangaModerationLoading] = useState(false);
    const [mangaModerationError, setMangaModerationError] = useState(null);
    const [mangaModerationActionMessage, setMangaModerationActionMessage] = useState(null);
    const [mangaTeamProfiles, setMangaTeamProfiles] = useState({});
    const [mangaRejectReasons, setMangaRejectReasons] = useState({});
    const [mangaTransferTeamId, setMangaTransferTeamId] = useState('');
    const [mangaTransferBusy, setMangaTransferBusy] = useState(false);
    const [teamQuery, setTeamQuery] = useState('');
    const [teamList, setTeamList] = useState([]);
    const [teamPendingOnly, setTeamPendingOnly] = useState(true);
    const [teamOrder, setTeamOrder] = useState('new');
    const [teamLoading, setTeamLoading] = useState(false);
    const [teamError, setTeamError] = useState(null);
    const [teamActionMessage, setTeamActionMessage] = useState(null);
    const [teamRejectReasons, setTeamRejectReasons] = useState({});
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [teamMangas, setTeamMangas] = useState([]);
    const [teamMangasLoading, setTeamMangasLoading] = useState(false);
    const [teamLogoUrls, setTeamLogoUrls] = useState({});
    const [teamAuditEntries, setTeamAuditEntries] = useState([]);
    const [teamAuditLoading, setTeamAuditLoading] = useState(false);
    const [teamAuditError, setTeamAuditError] = useState(null);
    const [teamAuditActionMessage, setTeamAuditActionMessage] = useState(null);
    const [teamAuditActionError, setTeamAuditActionError] = useState(null);
    const [teamAuditActionBusy, setTeamAuditActionBusy] = useState(false);
    const [mangaCoverUrls, setMangaCoverUrls] = useState({});
    const [lightboxImage, setLightboxImage] = useState(null);
    const [lightboxTitle, setLightboxTitle] = useState('');
    const [previewChapterId, setPreviewChapterId] = useState(null);
    const [previewPages, setPreviewPages] = useState([]);
    const [previewPageIndex, setPreviewPageIndex] = useState(0);
    const [previewPageUrl, setPreviewPageUrl] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState(null);

    const isAllowed = MangaApiService.isAllowedDomain();

    useEffect(() => {
        document.title = 'animix - Модерация манги';
    }, []);

    useEffect(() => {
        if (!isAllowed || activeTab !== 'chapters') {
            return;
        }
        let isActive = true;
        const loadChapters = async () => {
            setLoading(true);
            setError(null);
            setActionMessage(null);
            setPreviewChapterId(null);
            setPreviewPages([]);
            setPreviewPageIndex(0);
            setPreviewPageUrl(null);
            setPreviewError(null);
            try {
                const response = await MangaApiService.getModerationChapters({
                    status: pendingOnly ? 'PENDING' : undefined,
                    order: chapterOrder,
                    page: 0,
                    size: CHAPTERS_PAGE_SIZE,
                });
                const { items, last } = unwrapModerationPage(response);
                if (!isActive) {
                    return;
                }
                setChapters(items);
                setChapterPage(0);
                setChapterHasMore(!last);
                if (items.length > 0) {
                    setSelectedChapter((prev) => {
                        const prevId = getChapterId(prev);
                        const found = prevId ? items.find((chapter) => isSameId(getChapterId(chapter), prevId)) : null;
                        return found || items[0];
                    });
                } else {
                    setSelectedChapter(null);
                }
            } catch (err) {
                if (!isActive) {
                    return;
                }
                setError(getMangaErrorMessage(err, 'Не удалось загрузить главы.'));
                setChapters([]);
                setChapterHasMore(false);
                setSelectedChapter(null);
            } finally {
                if (isActive) {
                    setLoading(false);
                }
            }
        };
        loadChapters();
        return () => {
            isActive = false;
        };
    }, [pendingOnly, chapterOrder, isAllowed, activeTab]);

    useEffect(() => {
        if (!isAllowed || activeTab !== 'mangas') {
            return;
        }
        const trimmedQuery = mangaQuery.trim();
        const handle = setTimeout(async () => {
            setMangaModerationLoading(true);
            setMangaModerationError(null);
            setMangaModerationActionMessage(null);
            try {
                if (mangaInactiveOnly && !trimmedQuery) {
                    const response = await MangaApiService.getInactiveMangas({ days: MANGA_INACTIVITY_DAYS });
                    // Server already orders by the oldest activity first.
                    setMangaModerationList(unwrapList(response).map((entry) => ({
                        ...(entry?.manga || {}),
                        lastActivityAt: entry?.lastActivityAt,
                        inactiveDays: entry?.inactiveDays,
                    })));
                    return;
                }
                const response = trimmedQuery
                    ? await MangaApiService.searchMangas(trimmedQuery, { includeDeleted: true })
                    : await MangaApiService.getModerationMangas({
                        status: mangaPendingOnly ? 'PENDING' : undefined,
                        order: mangaOrder,
                    });
                const list = unwrapList(response);
                if (response?.type && response.type !== 'success') {
                    setMangaModerationList([]);
                    setMangaModerationError('Не удалось найти мангу.');
                } else {
                    setMangaModerationList(sortByCreatedAt(list, mangaOrder));
                }
            } catch (err) {
                setMangaModerationError(getMangaErrorMessage(err, 'Не удалось найти мангу.'));
            } finally {
                setMangaModerationLoading(false);
            }
        }, trimmedQuery ? 350 : 0);
        return () => clearTimeout(handle);
    }, [mangaQuery, mangaPendingOnly, mangaInactiveOnly, mangaOrder, isAllowed, activeTab]);

    useEffect(() => {
        if (!isAllowed || activeTab !== 'teams') {
            return;
        }
        const handle = setTimeout(async () => {
            setTeamLoading(true);
            setTeamError(null);
            setTeamActionMessage(null);
            try {
                const statusFilter = teamPendingOnly ? 'PENDING' : undefined;
                const response = await MangaApiService.getModerationTeams({
                    status: statusFilter,
                    order: teamOrder,
                });
                const list = unwrapList(response);
                if (response?.type && response.type !== 'success') {
                    setTeamList([]);
                    setTeamError('Не удалось найти команды.');
                } else {
                    setTeamList(sortByCreatedAt(list, teamOrder));
                }
            } catch (err) {
                setTeamError(getMangaErrorMessage(err, 'Не удалось найти команды.'));
            } finally {
                setTeamLoading(false);
            }
        }, 0);
        return () => clearTimeout(handle);
    }, [teamPendingOnly, teamOrder, isAllowed, activeTab]);

    useEffect(() => {
        if (!teamList.length) {
            return;
        }
        let cancelled = false;
        const loadLogos = async () => {
            const updates = {};
            for (const team of teamList) {
                const teamId = getTeamId(team) || team?.id;
                if (!teamId || teamLogoUrls[teamId]) {
                    continue;
                }
                const { fileId, bucket } = getTeamLogoMeta(team);
                if (!fileId || !bucket) {
                    continue;
                }
                const logoUrl = await MangaApiService.resolveFileUrlAsync({ fileId, bucket });
                if (logoUrl) {
                    updates[teamId] = logoUrl;
                }
            }
            if (!cancelled && Object.keys(updates).length) {
                setTeamLogoUrls((prev) => ({ ...prev, ...updates }));
            }
        };
        loadLogos();
        return () => {
            cancelled = true;
        };
    }, [teamList, teamLogoUrls]);

    useEffect(() => {
        if (!previewChapterId) {
            setPreviewPages([]);
            setPreviewPageIndex(0);
            setPreviewPageUrl(null);
            setPreviewError(null);
            setPreviewLoading(false);
            return;
        }
        let isActive = true;
        setPreviewLoading(true);
        setPreviewError(null);
        setPreviewPages([]);
        setPreviewPageIndex(0);
        const loadPages = async () => {
            try {
                const response = await MangaApiService.getChapterPages(previewChapterId);
                const list = unwrapPagesResponse(response);
                if (isActive) {
                    const normalized = normalizeChapterPages(list);
                    const sorted = [...normalized].sort((left, right) => {
                        const leftNum = getPageNumber(left, 0);
                        const rightNum = getPageNumber(right, 0);
                        return leftNum - rightNum;
                    });
                    setPreviewPages(sorted);
                }
            } catch (err) {
                if (isActive) {
                    setPreviewError(getMangaErrorMessage(err, 'Не удалось загрузить страницы главы.'));
                    setPreviewPages([]);
                }
            } finally {
                if (isActive) {
                    setPreviewLoading(false);
                }
            }
        };
        loadPages();
        return () => {
            isActive = false;
        };
    }, [previewChapterId]);

    useEffect(() => {
        let isActive = true;
        const resolvePreviewUrl = async () => {
            if (!previewChapterId || previewPages.length === 0) {
                setPreviewPageUrl(null);
                return;
            }
            const currentPage = previewPages[previewPageIndex];
            if (!currentPage) {
                setPreviewPageUrl(null);
                return;
            }
            const chapter = chapters.find((item) => {
                const id = item?.id || item?.chapterId || item?.chapter_id;
                return isSameId(id, previewChapterId);
            });
            const fallbackBucket = chapter?.pagesStorageBucket || chapter?.storageBucket || chapter?.bucket;
            const bucket = getPageBucket(currentPage, fallbackBucket);
            const direct = MangaApiService.resolveFileUrl(currentPage, bucket);
            if (direct) {
                setPreviewPageUrl(direct);
                return;
            }
            try {
                const resolved = await MangaApiService.resolveFileUrlAsync(currentPage, bucket);
                if (isActive) {
                    setPreviewPageUrl(resolved);
                }
            } catch (error) {
                if (isActive) {
                    setPreviewPageUrl(null);
                }
            }
        };
        resolvePreviewUrl();
        return () => {
            isActive = false;
        };
    }, [previewChapterId, previewPages, previewPageIndex, chapters]);

    useEffect(() => {
        if (!chapters.length) {
            return;
        }
        let cancelled = false;
        const loadChapterCovers = async () => {
            const updates = {};
            for (const chapter of chapters) {
                const mangaId = getChapterMangaId(chapter);
                if (!mangaId || mangaCoverUrls[mangaId] || updates[mangaId]) {
                    continue;
                }
                const coverMeta = getChapterCoverMeta(chapter);
                const direct = MangaApiService.resolveCoverUrl(coverMeta);
                if (direct) {
                    updates[mangaId] = direct;
                    continue;
                }
                const resolved = await MangaApiService.resolveCoverUrlAsync(coverMeta);
                if (resolved) {
                    updates[mangaId] = resolved;
                }
            }
            if (!cancelled && Object.keys(updates).length) {
                setMangaCoverUrls((prev) => ({ ...prev, ...updates }));
            }
        };
        loadChapterCovers();
        return () => {
            cancelled = true;
        };
    }, [chapters, mangaCoverUrls]);

    useEffect(() => {
        if (!mangaModerationList.length) {
            return;
        }
        let cancelled = false;
        const loadCovers = async () => {
            const updates = {};
            for (const manga of mangaModerationList) {
                const mangaId = getMangaId(manga);
                if (!mangaId || mangaCoverUrls[mangaId]) {
                    continue;
                }
                const direct = MangaApiService.resolveCoverUrl(manga);
                if (direct) {
                    updates[mangaId] = direct;
                    continue;
                }
                const resolved = await MangaApiService.resolveCoverUrlAsync(manga);
                if (resolved) {
                    updates[mangaId] = resolved;
                }
            }
            if (!cancelled && Object.keys(updates).length) {
                setMangaCoverUrls((prev) => ({ ...prev, ...updates }));
            }
        };
        loadCovers();
        return () => {
            cancelled = true;
        };
    }, [mangaModerationList, mangaCoverUrls]);

    useEffect(() => {
        if (!mangaModerationList.length) {
            return;
        }
        let cancelled = false;
        const loadTeams = async () => {
            const updates = {};
            for (const manga of mangaModerationList) {
                const teamId = getMangaTeamId(manga);
                if (!teamId || mangaTeamProfiles[teamId] || updates[teamId]) {
                    continue;
                }
                if (mangaTeamInFlight.has(teamId)) {
                    continue;
                }
                const request = MangaApiService.getTeam(teamId)
                    .then((response) => response?.data || response)
                    .then((payload) => payload?.data || payload?.team || payload)
                    .catch(() => null)
                    .finally(() => {
                        mangaTeamInFlight.delete(teamId);
                    });
                mangaTeamInFlight.set(teamId, request);
                const teamData = await request;
                if (teamData) {
                    updates[teamId] = teamData;
                }
            }
            if (!cancelled && Object.keys(updates).length) {
                setMangaTeamProfiles((prev) => ({ ...prev, ...updates }));
            }
        };
        loadTeams();
        return () => {
            cancelled = true;
        };
    }, [mangaModerationList, mangaTeamProfiles]);

    const handleSelectChapter = (chapter) => {
        setSelectedChapter(chapter);
        setActionMessage(null);
        setError(null);
        setPreviewChapterId(null);
        setPreviewPages([]);
        setPreviewPageIndex(0);
        setPreviewPageUrl(null);
        setPreviewError(null);
    };

    const loadMoreChapters = async () => {
        if (loading || !chapterHasMore) {
            return;
        }
        const nextPage = chapterPage + 1;
        setLoading(true);
        setError(null);
        try {
            const response = await MangaApiService.getModerationChapters({
                status: pendingOnly ? 'PENDING' : undefined,
                order: chapterOrder,
                page: nextPage,
                size: CHAPTERS_PAGE_SIZE,
            });
            const { items, last } = unwrapModerationPage(response);
            setChapters((prev) => [...prev, ...items]);
            setChapterPage(nextPage);
            setChapterHasMore(!last);
        } catch (err) {
            setError(getMangaErrorMessage(err, 'Не удалось загрузить главы.'));
        } finally {
            setLoading(false);
        }
    };

    const refreshModerationChapters = async (keepSelectionId = null) => {
        setLoading(true);
        setError(null);
        setPreviewChapterId(null);
        setPreviewPages([]);
        setPreviewPageIndex(0);
        setPreviewPageUrl(null);
        setPreviewError(null);
        try {
            const response = await MangaApiService.getModerationChapters({
                status: pendingOnly ? 'PENDING' : undefined,
                order: chapterOrder,
                page: 0,
                size: CHAPTERS_PAGE_SIZE,
            });
            const { items, last } = unwrapModerationPage(response);
            setChapters(items);
            setChapterPage(0);
            setChapterHasMore(!last);
            if (items.length > 0) {
                const targetId = keepSelectionId || getChapterId(selectedChapter);
                const found = targetId ? items.find((chapter) => isSameId(getChapterId(chapter), targetId)) : null;
                setSelectedChapter(found || items[0]);
            } else {
                setSelectedChapter(null);
            }
        } catch (err) {
            setError(getMangaErrorMessage(err, 'Не удалось загрузить главы.'));
            setChapters([]);
            setChapterHasMore(false);
            setSelectedChapter(null);
        } finally {
            setLoading(false);
        }
    };

    const filteredChapters = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        return chapters.filter((chapter) => {
            if (pendingOnly && !isPendingStatus(getChapterStatus(chapter))) {
                return false;
            }
            if (!query) {
                return true;
            }
            const number = getChapterNumber(chapter);
            const title = getChapterTitle(chapter);
            const mangaTitle = getChapterMangaTitle(chapter);
            const mangaSlug = getChapterMangaSlug(chapter);
            const mangaId = getChapterMangaId(chapter);
            const chapterId = getChapterId(chapter);
            const teamId = getChapterTeamId(chapter);
            const chapterStatus = getChapterStatus(chapter);
            const mangaStatus = getChapterMangaStatus(chapter);
            const haystack = [
                mangaTitle,
                mangaSlug,
                title,
                number,
                mangaId,
                chapterId,
                teamId,
                chapterStatus,
                mangaStatus,
            ].map((value) => String(value ?? '')).join(' ').toLowerCase();
            return haystack.includes(query);
        });
    }, [chapters, pendingOnly, searchQuery]);

    const handleApprove = async (chapterId) => {
        setActionMessage(null);
        setError(null);
        try {
            const response = await MangaApiService.approveChapter(chapterId);
            if (response?.type === 'success' || response) {
                setActionMessage('Глава одобрена.');
                await refreshModerationChapters(chapterId);
            } else {
                setError('Не удалось одобрить главу.');
            }
        } catch (err) {
            setError(getMangaErrorMessage(err, 'Не удалось одобрить главу.'));
        }
    };

    const handleReject = async (chapterId) => {
        const reason = (rejectReasons[chapterId] || '').trim();
        if (!reason) {
            setError('Для отклонения нужна причина.');
            return;
        }
        setActionMessage(null);
        setError(null);
        try {
            const response = await MangaApiService.rejectChapter(chapterId, reason);
            if (response?.type === 'success' || response) {
                setActionMessage('Глава отклонена.');
                setRejectReasons((prev) => ({
                    ...prev,
                    [chapterId]: '',
                }));
                await refreshModerationChapters(chapterId);
            } else {
                setError('Не удалось отклонить главу.');
            }
        } catch (err) {
            setError(getMangaErrorMessage(err, 'Не удалось отклонить главу.'));
        }
    };

    const handleRestoreChapter = async (chapterId) => {
        setActionMessage(null);
        setError(null);
        try {
            const response = await MangaApiService.restoreModerationChapter(chapterId);
            if (response?.type === 'success' || response) {
                setActionMessage('Глава восстановлена.');
                await refreshModerationChapters(chapterId);
            } else {
                setError('Не удалось восстановить главу.');
            }
        } catch (err) {
            setError(getMangaErrorMessage(err, 'Не удалось восстановить главу.'));
        }
    };

    const handleTogglePreview = (chapterId) => {
        if (!chapterId) {
            return;
        }
        if (previewChapterId && isSameId(previewChapterId, chapterId)) {
            setPreviewChapterId(null);
            return;
        }
        setPreviewChapterId(chapterId);
    };

    const filteredModerationMangas = useMemo(() => {
        if (!mangaPendingOnly || mangaInactiveOnly) {
            return mangaModerationList;
        }
        return mangaModerationList.filter((manga) => isPendingStatus(getMangaStatus(manga)));
    }, [mangaModerationList, mangaPendingOnly, mangaInactiveOnly]);

    const handleSelectModerationManga = (manga) => {
        setSelectedModerationManga(manga);
        setMangaModerationActionMessage(null);
        setMangaModerationError(null);
        setMangaTransferTeamId('');
    };

    const updateModerationMangaStatus = (mangaId, status) => {
        if (!mangaId) {
            return;
        }
        setMangaModerationList((prev) => prev.map((manga) => {
            const currentId = getMangaId(manga);
            if (!isSameId(currentId, mangaId)) {
                return manga;
            }
            return { ...manga, status };
        }));
        setSelectedModerationManga((prev) => {
            if (!prev) {
                return prev;
            }
            const currentId = getMangaId(prev);
            if (!isSameId(currentId, mangaId)) {
                return prev;
            }
            return { ...prev, status };
        });
    };

    const handleApproveManga = async (mangaId) => {
        if (!mangaId) {
            setMangaModerationError('Не указан идентификатор манги.');
            return;
        }
        setMangaModerationActionMessage(null);
        setMangaModerationError(null);
        try {
            const response = await MangaApiService.approveManga(mangaId);
            if (response?.type === 'success' || response) {
                setMangaModerationActionMessage('Манга одобрена.');
                updateModerationMangaStatus(mangaId, 'PUBLISHED');
            } else {
                setMangaModerationError('Не удалось одобрить мангу.');
            }
        } catch (err) {
            if (err?.response?.status === 409) {
                setMangaModerationError('Команда манги не одобрена. Сначала одобрите команду.');
                return;
            }
            setMangaModerationError(getMangaErrorMessage(err, 'Не удалось одобрить мангу.'));
        }
    };

    const handleRejectManga = async (mangaId) => {
        const reason = (mangaRejectReasons[mangaId] || '').trim();
        if (!reason) {
            setMangaModerationError('Для отклонения нужна причина.');
            return;
        }
        setMangaModerationActionMessage(null);
        setMangaModerationError(null);
        try {
            const response = await MangaApiService.rejectManga(mangaId, reason);
            if (response?.type === 'success' || response) {
                setMangaModerationActionMessage('Манга отклонена.');
                updateModerationMangaStatus(mangaId, 'REJECTED');
            } else {
                setMangaModerationError('Не удалось отклонить мангу.');
            }
        } catch (err) {
            setMangaModerationError(getMangaErrorMessage(err, 'Не удалось отклонить мангу.'));
        }
    };

    const handleTransferManga = async (mangaId) => {
        if (!mangaId) {
            setMangaModerationError('Не указан идентификатор манги.');
            return;
        }
        const targetTeamId = mangaTransferTeamId.trim();
        if (!targetTeamId) {
            setMangaModerationError('Укажите ID команды для переноса.');
            return;
        }
        setMangaModerationActionMessage(null);
        setMangaModerationError(null);
        setMangaTransferBusy(true);
        try {
            await MangaApiService.transferManga(mangaId, Number.isNaN(Number(targetTeamId)) ? targetTeamId : Number(targetTeamId));
            setMangaModerationActionMessage('Манга перенесена.');
            setMangaTransferTeamId('');
        } catch (err) {
            setMangaModerationError(getMangaErrorMessage(err, 'Не удалось перенести мангу.'));
        } finally {
            setMangaTransferBusy(false);
        }
    };

    const handleRestoreManga = async (mangaId) => {
        if (!mangaId) {
            setMangaModerationError('Не указан идентификатор манги.');
            return;
        }
        setMangaModerationActionMessage(null);
        setMangaModerationError(null);
        try {
            const response = await MangaApiService.restoreModerationManga(mangaId);
            if (response?.type === 'success' || response) {
                setMangaModerationActionMessage('Манга восстановлена.');
                const trimmedQuery = mangaQuery.trim();
                const refreshed = trimmedQuery
                    ? await MangaApiService.searchMangas(trimmedQuery, { includeDeleted: true })
                    : await MangaApiService.getModerationMangas({
                        status: mangaPendingOnly ? 'PENDING' : undefined,
                        order: mangaOrder,
                    });
                const list = unwrapList(refreshed);
                if (refreshed?.type && refreshed.type !== 'success') {
                    setMangaModerationError('Не удалось обновить список манги.');
                    setMangaModerationList([]);
                } else {
                    setMangaModerationList(sortByCreatedAt(list, mangaOrder));
                    const updated = list.find((item) => isSameId(getMangaId(item), mangaId));
                    if (updated) {
                        setSelectedModerationManga(updated);
                    }
                }
            } else {
                setMangaModerationError('Не удалось восстановить мангу.');
            }
        } catch (err) {
            setMangaModerationError(getMangaErrorMessage(err, 'Не удалось восстановить мангу.'));
        }
    };

    const filteredTeams = useMemo(() => (
        teamList.filter((team) => {
            if (teamPendingOnly && !isPendingStatus(getTeamStatus(team))) {
                return false;
            }
            if (!matchesTeamQuery(team, teamQuery.trim())) {
                return false;
            }
            return true;
        })
    ), [teamList, teamPendingOnly, teamQuery]);

    const loadTeamMangas = async (teamId) => {
        if (!teamId) {
            setTeamMangas([]);
            return;
        }
        setTeamMangasLoading(true);
        setTeamError(null);
        try {
            const response = await MangaApiService.getTeamMangas(teamId, { includeDeleted: true });
            const list = unwrapList(response);
            if (response?.type && response.type !== 'success') {
                setTeamError('Не удалось загрузить релизы команды.');
                setTeamMangas([]);
            } else {
                setTeamMangas(list);
            }
        } catch (err) {
            setTeamError(getMangaErrorMessage(err, 'Не удалось загрузить релизы команды.'));
            setTeamMangas([]);
        } finally {
            setTeamMangasLoading(false);
        }
    };

    const loadTeamAudit = async (teamId) => {
        if (!teamId) {
            setTeamAuditEntries([]);
            return;
        }
        setTeamAuditLoading(true);
        setTeamAuditError(null);
        try {
            const response = await MangaApiService.getModerationTeamAudit(teamId);
            const list = unwrapList(response);
            if (response?.type && response.type !== 'success') {
                setTeamAuditError('Не удалось загрузить аудит.');
                setTeamAuditEntries([]);
            } else {
                setTeamAuditEntries(list);
            }
        } catch (err) {
            setTeamAuditError(getMangaErrorMessage(err, 'Не удалось загрузить аудит.'));
            setTeamAuditEntries([]);
        } finally {
            setTeamAuditLoading(false);
        }
    };

    const handleAuditRollback = async (entry, teamId) => {
        const { mangaId, chapterId } = extractAuditTargets(entry);
        if (!mangaId && !chapterId) {
            setTeamAuditActionError('Не удалось определить объект для восстановления.');
            return;
        }
        setTeamAuditActionMessage(null);
        setTeamAuditActionError(null);
        setTeamAuditActionBusy(true);
        try {
            if (mangaId) {
                await MangaApiService.restoreModerationManga(mangaId);
                setTeamAuditActionMessage('Тайтл восстановлен.');
            } else {
                await MangaApiService.restoreModerationChapter(chapterId);
                setTeamAuditActionMessage('Глава восстановлена.');
            }
            if (teamId) {
                await Promise.all([loadTeamMangas(teamId), loadTeamAudit(teamId)]);
            }
        } catch (err) {
            setTeamAuditActionError(getMangaErrorMessage(err, 'Не удалось восстановить запись.'));
        } finally {
            setTeamAuditActionBusy(false);
        }
    };

    const handleSelectTeam = async (team) => {
        setSelectedTeam(team);
        setTeamActionMessage(null);
        setTeamError(null);
        const teamId = getTeamId(team) || team?.id;
        if (!teamId) {
            setTeamMangas([]);
            setTeamAuditEntries([]);
            return;
        }
        await Promise.all([loadTeamMangas(teamId), loadTeamAudit(teamId)]);
    };

    const updateTeamStatus = (teamId, status) => {
        if (!teamId) {
            return;
        }
        setTeamList((prev) => prev.map((team) => {
            const currentId = getTeamId(team) || team?.id;
            if (!isSameId(currentId, teamId)) {
                return team;
            }
            return { ...team, status };
        }));
        setSelectedTeam((prev) => {
            if (!prev) {
                return prev;
            }
            const currentId = getTeamId(prev) || prev?.id;
            if (!isSameId(currentId, teamId)) {
                return prev;
            }
            return { ...prev, status };
        });
    };

    const handleApproveTeam = async (teamId) => {
        if (!teamId) {
            setTeamError('Не указан идентификатор команды.');
            return;
        }
        setTeamActionMessage(null);
        setTeamError(null);
        try {
            const response = await MangaApiService.approveTeam(teamId);
            if (response?.type === 'success' || response) {
                setTeamActionMessage('Команда одобрена.');
                updateTeamStatus(teamId, 'APPROVED');
            } else {
                setTeamError('Не удалось одобрить команду.');
            }
        } catch (err) {
            setTeamError(getMangaErrorMessage(err, 'Не удалось одобрить команду.'));
        }
    };

    const handleRejectTeam = async (teamId) => {
        const reason = (teamRejectReasons[teamId] || '').trim();
        if (!reason) {
            setTeamError('Для отклонения нужна причина.');
            return;
        }
        setTeamActionMessage(null);
        setTeamError(null);
        try {
            const response = await MangaApiService.rejectTeam(teamId, reason);
            if (response?.type === 'success' || response) {
                setTeamActionMessage('Команда отклонена.');
                updateTeamStatus(teamId, 'REJECTED');
            } else {
                setTeamError('Не удалось отклонить команду.');
            }
        } catch (err) {
            setTeamError(getMangaErrorMessage(err, 'Не удалось отклонить команду.'));
        }
    };

    const handleDeleteTeam = async (teamId) => {
        if (!teamId) {
            setTeamError('Не указан идентификатор команды.');
            return;
        }
        if (!window.confirm('Удалить команду? Это удалит все тайтлы, которые она загрузила. Действие необратимо.')) {
            return;
        }
        setTeamActionMessage(null);
        setTeamError(null);
        try {
            await MangaApiService.deleteRejectedTeam(teamId);
            setTeamActionMessage('Команда удалена.');
            setTeamList((prev) => prev.filter((team) => {
                const currentId = getTeamId(team) || team?.id;
                return !isSameId(currentId, teamId);
            }));
            setSelectedTeam((prev) => {
                if (!prev) {
                    return prev;
                }
                const currentId = getTeamId(prev) || prev?.id;
                if (!isSameId(currentId, teamId)) {
                    return prev;
                }
                return null;
            });
        } catch (err) {
            const status = err?.response?.status;
            if (status === 409) {
                setTeamError('Удаление доступно только для отклонённых команд.');
            } else if (status === 404) {
                setTeamError('Команда не найдена.');
            } else {
                setTeamError(getMangaErrorMessage(err, 'Не удалось удалить команду.'));
            }
        }
    };

    const openLightbox = (src, title) => {
        if (!src) {
            return;
        }
        setLightboxImage(src);
        setLightboxTitle(title || 'Обложка манги');
    };

    const closeLightbox = () => {
        setLightboxImage(null);
        setLightboxTitle('');
    };

    const selectedTeamId = getTeamId(selectedTeam) || selectedTeam?.id;
    const selectedTeamName = selectedTeam ? getTeamName(selectedTeam, selectedTeamId) : '';
    const selectedTeamStatus = selectedTeam ? getTeamStatus(selectedTeam) : null;
    const selectedChapterId = getChapterId(selectedChapter);
    const selectedChapterNumber = getChapterNumber(selectedChapter);
    const selectedChapterTitle = getChapterTitle(selectedChapter);
    const selectedChapterStatus = getChapterStatus(selectedChapter);
    const selectedChapterLabel = selectedChapter
        ? (selectedChapterNumber ? `Глава ${selectedChapterNumber}` : 'Глава без номера')
        : '';
    const selectedChapterMangaTitle = getChapterMangaTitle(selectedChapter);
    const selectedChapterMangaSlug = getChapterMangaSlug(selectedChapter);
    const selectedChapterMangaId = getChapterMangaId(selectedChapter);
    const selectedChapterMangaStatus = getChapterMangaStatus(selectedChapter);
    const selectedChapterTeamId = getChapterTeamId(selectedChapter);
    const selectedChapterReason = getRejectionReason(selectedChapter);
    const selectedChapterCreatedAt = selectedChapter?.createdAt || selectedChapter?.created_at;
    const selectedChapterViews = selectedChapter?.views ?? selectedChapter?.viewCount ?? selectedChapter?.views_count;
    const selectedChapterCover = selectedChapter
        ? (selectedChapterMangaId
            ? (mangaCoverUrls[selectedChapterMangaId] || getChapterCoverUrl(selectedChapter))
            : getChapterCoverUrl(selectedChapter))
        : null;
    const selectedChapterIsDeleted = isDeletedEntry(selectedChapter, selectedChapterStatus);
    const isPreviewingSelected = selectedChapterId
        && previewChapterId
        && isSameId(previewChapterId, selectedChapterId);
    const previewPage = isPreviewingSelected ? previewPages[previewPageIndex] : null;
    const previewPageNumber = isPreviewingSelected && previewPages.length > 0
        ? getPageNumber(previewPage, previewPageIndex)
        : 0;
    const selectedModerationMangaId = getMangaId(selectedModerationManga);
    const selectedModerationMangaTitle = selectedModerationManga ? getMangaTitle(selectedModerationManga) : '';
    const selectedModerationMangaStatus = selectedModerationManga ? getMangaStatus(selectedModerationManga) : null;
    const selectedModerationMangaReason = getRejectionReason(selectedModerationManga);
    const selectedModerationMangaDeleted = isDeletedEntry(selectedModerationManga, selectedModerationMangaStatus);
    const selectedModerationMangaPublisher = getMangaPublisher(selectedModerationManga);
    const selectedModerationMangaAuthor = getMangaAuthor(selectedModerationManga);
    const selectedModerationMangaGenres = formatGenres(selectedModerationManga?.genres || selectedModerationManga?.tags);
    const selectedModerationMangaPublisherLabel = formatFieldValue(selectedModerationMangaPublisher);
    const selectedModerationMangaAuthorLabel = formatFieldValue(selectedModerationMangaAuthor);
    const selectedModerationMangaGenresLabel = formatFieldValue(selectedModerationMangaGenres);
    const selectedModerationMangaNsfwLabel = formatNsfwLabel(selectedModerationManga);
    const selectedModerationMangaTeamId = getMangaTeamId(selectedModerationManga);
    const selectedModerationMangaTeamProfile = selectedModerationMangaTeamId
        ? mangaTeamProfiles[selectedModerationMangaTeamId]
        : null;
    const selectedModerationMangaTeamName = selectedModerationMangaTeamProfile
        ? getTeamName(selectedModerationMangaTeamProfile, selectedModerationMangaTeamId)
        : getMangaTeamName(selectedModerationManga);
    const selectedModerationMangaTeamLabel = selectedModerationMangaTeamName && selectedModerationMangaTeamId
        && !String(selectedModerationMangaTeamName).includes(String(selectedModerationMangaTeamId))
        ? `${selectedModerationMangaTeamName} · #${selectedModerationMangaTeamId}`
        : (selectedModerationMangaTeamName || (selectedModerationMangaTeamId ? `Команда #${selectedModerationMangaTeamId}` : null));
    const selectedModerationMangaCover = selectedModerationMangaId
        ? (mangaCoverUrls[selectedModerationMangaId] || MangaApiService.resolveCoverUrl(selectedModerationManga))
        : null;
    const trimmedTeamQuery = teamQuery.trim();
    const selectedTeamLogo = selectedTeamId ? teamLogoUrls[selectedTeamId] : null;
    const chaptersEmptyMessage = searchQuery.trim()
        ? 'Главы по запросу не найдены.'
        : (pendingOnly && chapters.length > 0
            ? 'Нет глав на модерации. Снимите фильтр.'
            : 'Главы не найдены.');
    const moderationMangaEmptyMessage = mangaInactiveOnly && !mangaQuery.trim()
        ? `Нет опубликованных тайтлов без новых глав дольше ${MANGA_INACTIVITY_DAYS} дней.`
        : (mangaPendingOnly && mangaModerationList.length > 0
            ? 'Нет манги на модерации. Снимите фильтр.'
            : (mangaQuery.trim() ? 'Манга не найдена.' : 'Манга пока не загружена.'));
    const teamsEmptyMessage = trimmedTeamQuery
        ? 'Команды по запросу не найдены.'
        : (teamPendingOnly ? 'Нет команд на модерации. Снимите фильтр.' : 'Команды пока не загружены.');

    if (!isAllowed) {
        return (
            <AdminLayout>
                <MangaAccessNotice />
            </AdminLayout>
        );
    }

    return (
        <AdminLayout eyebrow="Контент" title="Манга" mainClassName="manga-admin-shell">
            <div className="ui-chips manga-admin-tabs" role="tablist" aria-label="Разделы модерации манги">
                <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === 'chapters'}
                    className="ui-chip manga-admin-tab"
                    onClick={() => setActiveTab('chapters')}
                >
                    <img src={chaptersIcon} alt="" aria-hidden="true" />
                    Главы
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === 'mangas'}
                    className="ui-chip manga-admin-tab"
                    onClick={() => setActiveTab('mangas')}
                >
                    <img src={mangaIcon} alt="" aria-hidden="true" />
                    Манги
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === 'teams'}
                    className="ui-chip manga-admin-tab"
                    onClick={() => setActiveTab('teams')}
                >
                    <img src={teamIcon} alt="" aria-hidden="true" />
                    Команды
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === 'reviews'}
                    className="ui-chip manga-admin-tab"
                    onClick={() => setActiveTab('reviews')}
                >
                    <span aria-hidden="true">★</span>
                    Отзывы
                </button>
            </div>

            {activeTab === 'reviews' ? <MangaReviewModeration /> : null}

            {activeTab === 'chapters' ? (
                <div className="admin-page">
                    <div className="anime-list">
                        <h2>Модерация глав</h2>
                        <div className="manga-help">
                            Список глав собирается автоматически. Используйте поиск по манге, главе или ID.
                        </div>
                        <input
                            type="text"
                            placeholder="Поиск по манге, главе или ID"
                            value={searchQuery}
                            onChange={(event) => setSearchQuery(event.target.value)}
                        />
                        {loading ? <div className="loader" /> : null}
                        <div className="anime-list-container">
                            {filteredChapters.length > 0 ? (
                                <>
                                    <ul>
                                        {filteredChapters.map((chapter) => {
                                            const chapterId = getChapterId(chapter);
                                            const chapterNumber = getChapterNumber(chapter);
                                            const chapterTitle = getChapterTitle(chapter);
                                            const chapterLabel = chapterNumber ? `Глава ${chapterNumber}` : 'Глава без номера';
                                            const status = getChapterStatus(chapter);
                                            const statusClass = getStatusClass(status);
                                            const mangaTitle = getChapterMangaTitle(chapter) || 'Манга';
                                            const mangaSlug = getChapterMangaSlug(chapter);
                                            const mangaId = getChapterMangaId(chapter);
                                            const teamId = getChapterTeamId(chapter);
                                            const isActive = selectedChapterId && isSameId(chapterId, selectedChapterId);
                                            const coverUrl = mangaId
                                                ? (mangaCoverUrls[mangaId] || getChapterCoverUrl(chapter))
                                                : getChapterCoverUrl(chapter);
                                            return (
                                                <li
                                                    key={chapterId || `${mangaId || mangaTitle}-${chapterLabel}`}
                                                    className={`manga-admin-list-item${isActive ? ' active' : ''}`}
                                                    onClick={() => handleSelectChapter(chapter)}
                                                >
                                                    <div className="manga-admin-list-row">
                                                        <div className="manga-cover-thumb static">
                                                            <img
                                                                src={coverUrl || placeholderCover}
                                                                alt={coverUrl ? mangaTitle : 'Обложка отсутствует'}
                                                                loading="lazy"
                                                            />
                                                        </div>
                                                        <div className="manga-admin-list-content">
                                                            <div className="manga-admin-title-row">
                                                                <span>{mangaTitle}</span>
                                                                {status ? (
                                                                    <span className={`manga-admin-status-pill status-${statusClass}`}>
                                                                        {translateStatus(status)}
                                                                    </span>
                                                                ) : null}
                                                            </div>
                                                            <div className="manga-admin-team-meta">
                                                                {chapterLabel}{chapterTitle ? ` · ${chapterTitle}` : ''}
                                                            </div>
                                                            {mangaSlug ? <div className="manga-admin-team-meta">Адрес: {mangaSlug}</div> : null}
                                                            {mangaId ? <div className="manga-admin-team-meta">Манга ID: {mangaId}</div> : null}
                                                            {chapterId ? <div className="manga-admin-team-meta">Глава ID: {chapterId}</div> : null}
                                                            {teamId ? <div className="manga-admin-team-meta">Команда: {teamId}</div> : null}
                                                        </div>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                    {chapterHasMore ? (
                                        <div className="manga-studio-toolbar">
                                            <button
                                                type="button"
                                                className="ui-btn ui-btn--secondary ui-btn--sm"
                                                onClick={loadMoreChapters}
                                                disabled={loading}
                                            >
                                                {loading ? 'Загрузка...' : 'Показать еще'}
                                            </button>
                                        </div>
                                    ) : null}
                                </>
                            ) : (
                                <>
                                    <div className="manga-empty">{chaptersEmptyMessage}</div>
                                    {chapterHasMore ? (
                                        <div className="manga-studio-toolbar">
                                            <button
                                                type="button"
                                                className="ui-btn ui-btn--secondary ui-btn--sm"
                                                onClick={loadMoreChapters}
                                                disabled={loading}
                                            >
                                                {loading ? 'Загрузка...' : 'Показать еще'}
                                            </button>
                                        </div>
                                    ) : null}
                                </>
                            )}
                        </div>
                    </div>

                    <div className="anime-details">
                        <div className="manga-admin-header">
                            <h2>Глава</h2>
                            <div className="manga-admin-toggle">
                                <input
                                    type="checkbox"
                                    id="pending-only"
                                    checked={pendingOnly}
                                    onChange={(event) => setPendingOnly(event.target.checked)}
                                />
                                <label className="label-switch" htmlFor="pending-only" />
                                <span className="label-switch-text">Только на модерации</span>
                                <select
                                    className="manga-admin-order"
                                    value={chapterOrder}
                                    onChange={(event) => setChapterOrder(event.target.value)}
                                >
                                    {ORDER_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        {selectedChapter ? (
                            <p className="manga-inline-note">
                                Выбрано: {selectedChapterMangaTitle || 'Манга'}
                                {selectedChapterLabel ? ` · ${selectedChapterLabel}` : ''}
                            </p>
                        ) : (
                            <div className="manga-help">
                                Выберите главу слева, чтобы открыть карточку.
                            </div>
                        )}
                        {error ? <div className="manga-inline-warning">{error}</div> : null}
                        {actionMessage ? <div className="manga-inline-note">{actionMessage}</div> : null}
                        {selectedChapter ? (
                            <div>
                                <div className="manga-admin-card">
                                    <div className="card-details">
                                        <div className="manga-admin-cover-block">
                                            <button
                                                type="button"
                                                className={`manga-cover-thumb large${selectedChapterCover ? '' : ' is-empty'}`}
                                                onClick={() => openLightbox(selectedChapterCover, selectedChapterMangaTitle || selectedChapterLabel)}
                                                disabled={!selectedChapterCover}
                                            >
                                                <img
                                                    src={selectedChapterCover || placeholderCover}
                                                    alt={selectedChapterMangaTitle || 'Обложка манги'}
                                                    loading="lazy"
                                                />
                                            </button>
                                            <div className="manga-admin-cover-details">
                                                <div className="manga-admin-title-row">
                                                    <div className="manga-admin-card-title">
                                                        {selectedChapterLabel || 'Глава'}
                                                    </div>
                                                    {selectedChapterStatus ? (
                                                        <span className={`manga-admin-status-pill status-${getStatusClass(selectedChapterStatus)}`}>
                                                            {translateStatus(selectedChapterStatus)}
                                                        </span>
                                                    ) : null}
                                                </div>
                                                {selectedChapterTitle ? (
                                                    <div className="manga-admin-meta">Название: {selectedChapterTitle}</div>
                                                ) : null}
                                                {selectedChapterMangaTitle ? (
                                                    <div className="manga-admin-meta">Манга: {selectedChapterMangaTitle}</div>
                                                ) : null}
                                                {selectedChapterMangaSlug ? (
                                                    <div className="manga-admin-meta">Адрес: {selectedChapterMangaSlug}</div>
                                                ) : null}
                                                {selectedChapterMangaStatus ? (
                                                    <div className="manga-admin-meta">
                                                        Статус манги: {translateStatus(selectedChapterMangaStatus)}
                                                    </div>
                                                ) : null}
                                                {selectedChapterMangaId ? (
                                                    <div className="manga-admin-meta">Manga ID: {selectedChapterMangaId}</div>
                                                ) : null}
                                                {selectedChapterId ? (
                                                    <div className="manga-admin-meta">Chapter ID: {selectedChapterId}</div>
                                                ) : null}
                                                {selectedChapterTeamId ? (
                                                    <div className="manga-admin-meta">Команда: {selectedChapterTeamId}</div>
                                                ) : null}
                                                {selectedChapterViews !== null && selectedChapterViews !== undefined ? (
                                                    <div className="manga-admin-meta">Просмотры: {selectedChapterViews}</div>
                                                ) : null}
                                                {selectedChapterCreatedAt ? (
                                                    <div className="manga-admin-meta">
                                                        Создана: {new Date(selectedChapterCreatedAt).toLocaleString()}
                                                    </div>
                                                ) : null}
                                                {selectedChapterReason ? (
                                                    <div className="manga-admin-meta">Причина: {selectedChapterReason}</div>
                                                ) : null}
                                            </div>
                                        </div>
                                        <div className="manga-admin-actions">
                                            <button className="ui-btn ui-btn--sm" onClick={() => handleApprove(selectedChapterId)} disabled={!selectedChapterId}>
                                                Одобрить
                                            </button>
                                            <button
                                                className="ui-btn ui-btn--secondary ui-btn--sm"
                                                onClick={() => handleReject(selectedChapterId)}
                                                disabled={!selectedChapterId}
                                            >
                                                Отклонить
                                            </button>
                                            {selectedChapterIsDeleted ? (
                                                <button
                                                    className="ui-btn ui-btn--secondary ui-btn--sm"
                                                    onClick={() => handleRestoreChapter(selectedChapterId)}
                                                    disabled={!selectedChapterId}
                                                >
                                                    Восстановить
                                                </button>
                                            ) : null}
                                            <button
                                                className="ui-btn ui-btn--secondary ui-btn--sm"
                                                onClick={() => handleTogglePreview(selectedChapterId)}
                                                disabled={!selectedChapterId}
                                            >
                                                {isPreviewingSelected ? 'Скрыть предпросмотр' : 'Предпросмотр'}
                                            </button>
                                            <input
                                                type="text"
                                                placeholder="Причина отклонения"
                                                value={rejectReasons[selectedChapterId] || ''}
                                                onChange={(event) => setRejectReasons((prev) => ({
                                                    ...prev,
                                                    [selectedChapterId]: event.target.value,
                                                }))}
                                                disabled={!selectedChapterId}
                                            />
                                        </div>
                                    </div>
                                </div>
                                {isPreviewingSelected ? (
                                    <div className="manga-admin-card">
                                        <div className="card-details">
                                            <div className="manga-reader">
                                                {previewError ? (
                                                    <div className="manga-inline-warning">{previewError}</div>
                                                ) : null}
                                                <div className="manga-reader__toolbar">
                                                    <button
                                                        className="ui-btn ui-btn--secondary ui-btn--sm"
                                                        onClick={() => setPreviewPageIndex(Math.max(0, previewPageIndex - 1))}
                                                        disabled={previewPageIndex === 0}
                                                    >
                                                        Назад
                                                    </button>
                                                    <span className="manga-reader__counter">
                                                        Страница {previewPageNumber || 0} из {previewPages.length || 0}
                                                    </span>
                                                    <button
                                                        className="ui-btn ui-btn--secondary ui-btn--sm"
                                                        onClick={() => setPreviewPageIndex(Math.min(previewPages.length - 1, previewPageIndex + 1))}
                                                        disabled={previewPageIndex >= previewPages.length - 1}
                                                    >
                                                        Вперёд
                                                    </button>
                                                </div>
                                                <div className="manga-reader__page">
                                                    {previewLoading ? (
                                                        <div className="loader" />
                                                    ) : previewPageUrl ? (
                                                        <img src={previewPageUrl} alt={`Страница ${previewPageNumber}`} />
                                                    ) : (
                                                        <div className="manga-empty">Страница недоступна.</div>
                                                    )}
                                                    {previewPages.length > 0 ? (
                                                        <>
                                                            <button
                                                                type="button"
                                                                className="manga-reader__nav manga-reader__nav--left"
                                                                onClick={() => setPreviewPageIndex(Math.max(0, previewPageIndex - 1))}
                                                                disabled={previewPageIndex === 0}
                                                                aria-label="Предыдущая страница"
                                                            />
                                                            <button
                                                                type="button"
                                                                className="manga-reader__nav manga-reader__nav--right"
                                                                onClick={() => setPreviewPageIndex(Math.min(previewPages.length - 1, previewPageIndex + 1))}
                                                                disabled={previewPageIndex >= previewPages.length - 1}
                                                                aria-label="Следующая страница"
                                                            />
                                                        </>
                                                    ) : null}
                                                </div>
                                                {previewPages.length > 0 ? (
                                                    <div className="manga-page-thumbs">
                                                        {previewPages.map((page, index) => {
                                                            const pageNumber = getPageNumber(page, index);
                                                            return (
                                                                <button
                                                                    key={`${getPageFileId(page) || pageNumber}-${index}`}
                                                                    className={`manga-page-thumb ${index === previewPageIndex ? 'active' : ''}`}
                                                                    onClick={() => setPreviewPageIndex(index)}
                                                                >
                                                                    {pageNumber}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        ) : (
                            <div className="manga-empty">Глава не выбрана.</div>
                        )}
                    </div>
                </div>
            ) : null}

            {activeTab === 'mangas' ? (
                <div className="admin-page">
                    <div className="anime-list">
                        <h2>Модерация манги</h2>
                        <div className="manga-help">
                            Найдите тайтл и одобрите или отклоните его. Фильтр «Заброшенные» показывает опубликованные тайтлы без новых глав дольше {MANGA_INACTIVITY_DAYS} дней — их можно перенести в другую команду.
                        </div>
                        <input
                            type="text"
                            placeholder="Поиск по манге"
                            value={mangaQuery}
                            onChange={(event) => setMangaQuery(event.target.value)}
                        />
                        {mangaModerationLoading ? <div className="loader" /> : null}
                        <div className="anime-list-container">
                            {filteredModerationMangas.length > 0 ? (
                                <ul>
                                    {filteredModerationMangas.map((manga) => {
                                        const mangaId = getMangaId(manga);
                                        const title = getMangaTitle(manga);
                                        const status = getMangaStatus(manga);
                                        const statusClass = getStatusClass(status);
                                        const isActive = selectedModerationMangaId && isSameId(mangaId, selectedModerationMangaId);
                                        const teamId = getMangaTeamId(manga);
                                        const teamProfile = teamId ? mangaTeamProfiles[teamId] : null;
                                        const fallbackTeamName = getMangaTeamName(manga);
                                        const teamName = teamProfile
                                            ? getTeamName(teamProfile, teamId)
                                            : (fallbackTeamName || (teamId ? `Команда #${teamId}` : null));
                                        const teamLabel = teamName && teamId && !String(teamName).includes(String(teamId))
                                            ? `${teamName} · #${teamId}`
                                            : teamName;
                                        const publisher = getMangaPublisher(manga);
                                        const author = getMangaAuthor(manga);
                                        const genres = formatGenres(manga?.genres || manga?.tags);
                                        const publisherLabel = formatFieldValue(publisher);
                                        const authorLabel = formatFieldValue(author);
                                        const genresLabel = formatFieldValue(genres);
                                        const nsfwLabel = formatNsfwLabel(manga);
                                        const coverUrl = mangaId
                                            ? (mangaCoverUrls[mangaId] || MangaApiService.resolveCoverUrl(manga))
                                            : MangaApiService.resolveCoverUrl(manga);
                                        return (
                                            <li
                                                key={mangaId || title}
                                                className={`manga-admin-list-item${isActive ? ' active' : ''}`}
                                                onClick={() => handleSelectModerationManga(manga)}
                                            >
                                                <div className="manga-admin-list-row">
                                                    <div className="manga-cover-thumb static">
                                                        <img
                                                            src={coverUrl || placeholderCover}
                                                            alt={coverUrl ? title : 'Обложка отсутствует'}
                                                            loading="lazy"
                                                        />
                                                    </div>
                                                        <div className="manga-admin-list-content">
                                                            <div className="manga-admin-title-row">
                                                                <span>{title}</span>
                                                                {status ? (
                                                                    <span className={`manga-admin-status-pill status-${statusClass}`}>
                                                                        {translateStatus(status)}
                                                                    </span>
                                                                ) : null}
                                                            </div>
                                                            {teamLabel ? <div className="manga-admin-team-meta">Команда: {teamLabel}</div> : null}
                                                            {manga?.lastActivityAt ? (
                                                                <div className="manga-admin-team-meta">
                                                                    Нет новых глав {manga.inactiveDays} дн. (с {new Date(manga.lastActivityAt).toLocaleDateString()})
                                                                </div>
                                                            ) : null}
                                                            <div className="manga-admin-team-meta">Издатель: {publisherLabel}</div>
                                                            <div className="manga-admin-team-meta">Автор: {authorLabel}</div>
                                                            <div className="manga-admin-team-meta">Жанры: {genresLabel}</div>
                                                            <div className="manga-admin-team-meta">NSFW: {nsfwLabel}</div>
                                                            <div className="manga-admin-team-meta">Перевод: {manga?.completed ? 'завершён' : 'выходит'}</div>
                                                            {manga?.slug ? <div className="manga-admin-team-meta">Адрес: {manga.slug}</div> : null}
                                                            {mangaId ? <div className="manga-admin-team-meta">ID: {mangaId}</div> : null}
                                                        </div>
                                                    </div>
                                                </li>
                                        );
                                    })}
                                </ul>
                            ) : (
                                <div className="manga-empty">{moderationMangaEmptyMessage}</div>
                            )}
                        </div>
                    </div>

                    <div className="anime-details">
                        <div className="manga-admin-header">
                            <h2>Тайтл</h2>
                            <div className="manga-admin-toggle">
                                <input
                                    type="checkbox"
                                    id="manga-pending-only"
                                    checked={mangaPendingOnly}
                                    onChange={(event) => {
                                        setMangaPendingOnly(event.target.checked);
                                        if (event.target.checked) {
                                            setMangaInactiveOnly(false);
                                        }
                                    }}
                                />
                                <label className="label-switch" htmlFor="manga-pending-only" />
                                <span className="label-switch-text">Только на модерации</span>
                                <input
                                    type="checkbox"
                                    id="manga-inactive-only"
                                    checked={mangaInactiveOnly}
                                    onChange={(event) => {
                                        setMangaInactiveOnly(event.target.checked);
                                        if (event.target.checked) {
                                            setMangaPendingOnly(false);
                                        }
                                    }}
                                />
                                <label className="label-switch" htmlFor="manga-inactive-only" />
                                <span className="label-switch-text">Заброшенные ({MANGA_INACTIVITY_DAYS}+ дн.)</span>
                                <select
                                    className="manga-admin-order"
                                    value={mangaOrder}
                                    onChange={(event) => setMangaOrder(event.target.value)}
                                >
                                    {ORDER_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        {selectedModerationManga ? (
                            <p className="manga-inline-note">
                                Выбрано: {selectedModerationMangaTitle}
                            </p>
                        ) : (
                            <div className="manga-help">
                                Выберите тайтл слева, чтобы открыть карточку.
                            </div>
                        )}
                        {mangaModerationError ? <div className="manga-inline-warning">{mangaModerationError}</div> : null}
                        {mangaModerationActionMessage ? <div className="manga-inline-note">{mangaModerationActionMessage}</div> : null}
                        {selectedModerationManga ? (
                            <div className="manga-admin-card">
                                <div className="card-details">
                                    <div className="manga-admin-cover-block">
                                        <button
                                            type="button"
                                            className={`manga-cover-thumb large${selectedModerationMangaCover ? '' : ' is-empty'}`}
                                            onClick={() => openLightbox(selectedModerationMangaCover, selectedModerationMangaTitle)}
                                            disabled={!selectedModerationMangaCover}
                                        >
                                            <img
                                                src={selectedModerationMangaCover || placeholderCover}
                                                alt={selectedModerationMangaTitle || 'Обложка манги'}
                                                loading="lazy"
                                            />
                                        </button>
                                            <div className="manga-admin-cover-details">
                                                <div className="manga-admin-title-row">
                                                    <div className="manga-admin-card-title">
                                                        {selectedModerationMangaTitle || 'Манга'}
                                                    </div>
                                                    {selectedModerationMangaStatus ? (
                                                        <span className={`manga-admin-status-pill status-${getStatusClass(selectedModerationMangaStatus)}`}>
                                                            {translateStatus(selectedModerationMangaStatus)}
                                                        </span>
                                                    ) : null}
                                                </div>
                                                {selectedModerationMangaTeamLabel ? (
                                                    <div className="manga-admin-team-meta">Команда: {selectedModerationMangaTeamLabel}</div>
                                                ) : null}
                                                <div className="manga-admin-team-meta">
                                                    Издатель: {selectedModerationMangaPublisherLabel}
                                                </div>
                                                <div className="manga-admin-team-meta">
                                                    Автор: {selectedModerationMangaAuthorLabel}
                                                </div>
                                                <div className="manga-admin-team-meta">
                                                    Жанры: {selectedModerationMangaGenresLabel}
                                                </div>
                                                <div className="manga-admin-team-meta">
                                                    NSFW: {selectedModerationMangaNsfwLabel}
                                                </div>
                                                <div className="manga-admin-team-meta">
                                                    Перевод: {selectedModerationManga?.completed ? 'завершён' : 'выходит'}
                                                </div>
                                                {selectedModerationManga?.description ? (
                                                    <div className="manga-help">{selectedModerationManga.description}</div>
                                                ) : null}
                                            {isRejectedStatus(selectedModerationMangaStatus) && selectedModerationMangaReason ? (
                                                <div className="manga-inline-warning manga-status-reason">
                                                    Причина отклонения: {selectedModerationMangaReason}
                                                </div>
                                            ) : null}
                                            {selectedModerationMangaCover ? (
                                                <button
                                                    type="button"
                                                    className="button-sec manga-cover-action"
                                                    onClick={() => openLightbox(selectedModerationMangaCover, selectedModerationMangaTitle)}
                                                >
                                                    На весь экран
                                                </button>
                                            ) : null}
                                        </div>
                                    </div>
                                    <div className="manga-admin-actions">
                                        <button className="ui-btn ui-btn--sm" onClick={() => handleApproveManga(selectedModerationMangaId)} disabled={!selectedModerationMangaId}>
                                            Одобрить
                                        </button>
                                        <button className="ui-btn ui-btn--secondary ui-btn--sm" onClick={() => handleRejectManga(selectedModerationMangaId)} disabled={!selectedModerationMangaId}>
                                            Отклонить
                                        </button>
                                        {selectedModerationMangaDeleted ? (
                                            <button className="ui-btn ui-btn--secondary ui-btn--sm" onClick={() => handleRestoreManga(selectedModerationMangaId)} disabled={!selectedModerationMangaId}>
                                                Восстановить
                                            </button>
                                        ) : null}
                                        <input
                                            type="text"
                                            placeholder="Причина отклонения"
                                            value={mangaRejectReasons[selectedModerationMangaId] || ''}
                                            onChange={(event) => setMangaRejectReasons((prev) => ({
                                                ...prev,
                                                [selectedModerationMangaId]: event.target.value,
                                            }))}
                                            disabled={!selectedModerationMangaId}
                                        />
                                        <input
                                            type="text"
                                            placeholder="ID команды для переноса"
                                            value={mangaTransferTeamId}
                                            onChange={(event) => setMangaTransferTeamId(event.target.value)}
                                            disabled={!selectedModerationMangaId || mangaTransferBusy}
                                        />
                                        <button
                                            className="ui-btn ui-btn--secondary ui-btn--sm"
                                            onClick={() => handleTransferManga(selectedModerationMangaId)}
                                            disabled={!selectedModerationMangaId || mangaTransferBusy}
                                        >
                                            {mangaTransferBusy ? 'Перенос...' : 'Перенести тайтл'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="manga-empty">Манга не выбрана.</div>
                        )}
                    </div>
                </div>
            ) : null}

            {activeTab === 'teams' ? (
                <div className="admin-page">
                    <div className="anime-list">
                        <h2>Модерация команд</h2>
                        <div className="manga-help">
                            Список команд приходит из модерации. Поиск фильтрует по названию и id.
                        </div>
                        <input
                            type="text"
                            placeholder="Поиск команды или манги"
                            value={teamQuery}
                            onChange={(event) => setTeamQuery(event.target.value)}
                        />
                        {teamLoading ? <div className="loader" /> : null}
                        <div className="anime-list-container">
                            {filteredTeams.length > 0 ? (
                                <ul>
                                    {filteredTeams.map((team) => {
                                        const teamId = getTeamId(team) || team?.id;
                                        const teamName = getTeamName(team, teamId);
                                        const status = getTeamStatus(team);
                                        const statusClass = getStatusClass(status);
                                        const isActive = selectedTeamId && isSameId(teamId, selectedTeamId);
                                        const logoUrl = teamId ? teamLogoUrls[teamId] : null;
                                        const mangaCount = Number.isFinite(team.mangaCount)
                                            ? team.mangaCount
                                            : (Number.isFinite(team.manga_count)
                                                ? team.manga_count
                                                : (Number.isFinite(team.mangasCount)
                                                    ? team.mangasCount
                                                    : (Number.isFinite(team.mangas_count)
                                                        ? team.mangas_count
                                                        : null)));
                                        return (
                                            <li
                                                key={teamId || teamName}
                                                className={`manga-admin-list-item${isActive ? ' active' : ''}`}
                                                onClick={() => handleSelectTeam(team)}
                                            >
                                                <div className="manga-admin-team-row">
                                                    <div className="manga-admin-team-avatar">
                                                        {logoUrl ? (
                                                            <img src={logoUrl} alt={teamName} />
                                                        ) : (
                                                            <img src={teamIcon} alt="" aria-hidden="true" />
                                                        )}
                                                    </div>
                                                    <div className="manga-admin-team-info">
                                                        <div className="manga-admin-team-title">
                                                            <span>{teamName}</span>
                                                            {status ? (
                                                                <span className={`manga-admin-status-pill status-${statusClass}`}>
                                                                    {translateStatus(status)}
                                                                </span>
                                                            ) : null}
                                                        </div>
                                                        {teamId ? <div className="manga-admin-team-meta">ID: {teamId}</div> : null}
                                                        {Number.isFinite(mangaCount) ? (
                                                            <div className="manga-admin-team-meta">Релизов: {mangaCount}</div>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            ) : (
                                <div className="manga-empty">{teamsEmptyMessage}</div>
                            )}
                        </div>
                    </div>

                    <div className="anime-details">
                        <div className="manga-admin-header">
                            <h2>Команда</h2>
                            <div className="manga-admin-toggle">
                                <input
                                    type="checkbox"
                                    id="teams-pending-only"
                                    checked={teamPendingOnly}
                                    onChange={(event) => setTeamPendingOnly(event.target.checked)}
                                />
                                <label className="label-switch" htmlFor="teams-pending-only" />
                                <span className="label-switch-text">Только на модерации</span>
                                <select
                                    className="manga-admin-order"
                                    value={teamOrder}
                                    onChange={(event) => setTeamOrder(event.target.value)}
                                >
                                    {ORDER_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        {selectedTeam ? (
                            <p className="manga-inline-note">
                                Выбрано: {selectedTeamName}
                            </p>
                        ) : (
                            <div className="manga-help">
                                Выберите команду слева, чтобы открыть карточку.
                            </div>
                        )}
                        {teamError ? <div className="manga-inline-warning">{teamError}</div> : null}
                        {teamActionMessage ? <div className="manga-inline-note">{teamActionMessage}</div> : null}
                        {selectedTeam ? (
                            <div>
                                <div className="manga-admin-card">
                                    <div className="card-details">
                                        <div className="manga-admin-team-hero">
                                            <div className="manga-admin-team-avatar large">
                                                {selectedTeamLogo ? (
                                                    <img src={selectedTeamLogo} alt={selectedTeamName || 'Команда'} />
                                                ) : (
                                                    <img src={teamIcon} alt="" aria-hidden="true" />
                                                )}
                                            </div>
                                            <div className="manga-admin-team-hero-content">
                                                <div className="manga-admin-title-row">
                                                    <div className="manga-admin-card-title">{selectedTeamName || 'Команда'}</div>
                                                    {selectedTeamStatus ? (
                                                        <span className={`manga-admin-status-pill status-${getStatusClass(selectedTeamStatus)}`}>
                                                            {translateStatus(selectedTeamStatus)}
                                                        </span>
                                                    ) : null}
                                                </div>
                                                {selectedTeamId ? <div className="manga-admin-meta">ID: {selectedTeamId}</div> : null}
                                                {selectedTeam?.description ? (
                                                    <div className="manga-help">{selectedTeam.description}</div>
                                                ) : null}
                                                {resolveDonationLinks(selectedTeam).length > 0 ? (
                                                    <div className="manga-studio-links">
                                                        <div className="manga-studio-links-title">Донаты</div>
                                                        {resolveDonationLinks(selectedTeam).map((link) => (
                                                            <a
                                                                key={link}
                                                                href={link}
                                                                rel="noreferrer"
                                                                onClick={(event) => {
                                                                    event.preventDefault();
                                                                    ExternalLinkService.openExternal(link);
                                                                }}
                                                            >
                                                                {link}
                                                            </a>
                                                        ))}
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                        <div className="manga-admin-actions">
                                            <button className="ui-btn ui-btn--sm" onClick={() => handleApproveTeam(selectedTeamId)} disabled={!selectedTeamId}>
                                                Одобрить
                                            </button>
                                            <button className="ui-btn ui-btn--secondary ui-btn--sm" onClick={() => handleRejectTeam(selectedTeamId)} disabled={!selectedTeamId}>
                                                Отклонить
                                            </button>
                                            {isRejectedStatus(selectedTeamStatus) ? (
                                                <button className="ui-btn ui-btn--sm admin-danger-button" onClick={() => handleDeleteTeam(selectedTeamId)} disabled={!selectedTeamId}>
                                                    Удалить команду
                                                </button>
                                            ) : null}
                                            <input
                                                type="text"
                                                placeholder="Причина отклонения"
                                                value={teamRejectReasons[selectedTeamId] || ''}
                                                onChange={(event) => setTeamRejectReasons((prev) => ({
                                                    ...prev,
                                                    [selectedTeamId]: event.target.value,
                                                }))}
                                                disabled={!selectedTeamId}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="manga-section">
                                    <div className="manga-admin-title-row">
                                        <div className="manga-admin-card-title">Релизы команды</div>
                                    </div>
                                    {teamMangasLoading ? <div className="loader" /> : null}
                                    {teamMangas.length > 0 ? (
                                        <div className="manga-studio-list">
                                            {teamMangas.map((manga) => {
                                                const mangaStatus = manga?.status || manga?.state || manga?.moderationStatus;
                                                const mangaStatusClass = getStatusClass(mangaStatus);
                                                const mangaTitle = manga?.title || manga?.name || manga?.slug || 'Без названия';
                                                const mangaKey = manga?.id || manga?.slug || mangaTitle;
                                                return (
                                                    <div key={mangaKey} className="manga-studio-release-item">
                                                        <div>
                                                            <div className="manga-studio-item-title">{mangaTitle}</div>
                                                            {manga?.slug || manga?.id ? (
                                                                <div className="manga-studio-item-meta">
                                                                    {manga?.slug ? `Адрес: ${manga.slug}` : `ID: ${manga.id}`}
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                        {mangaStatus ? (
                                                            <span className={`manga-admin-status-pill status-${mangaStatusClass}`}>
                                                                {translateStatus(mangaStatus)}
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="manga-empty">Релизы не найдены.</div>
                                    )}
                                </div>

                                <div className="manga-section">
                                    <div className="manga-admin-title-row">
                                        <div className="manga-admin-card-title">Аудит (7 дней)</div>
                                    </div>
                                    {teamAuditLoading ? <div className="loader" /> : null}
                                    {teamAuditError ? <div className="manga-inline-warning">{teamAuditError}</div> : null}
                                    {teamAuditActionMessage ? <div className="manga-inline-note">{teamAuditActionMessage}</div> : null}
                                    {teamAuditActionError ? <div className="manga-inline-warning">{teamAuditActionError}</div> : null}
                                    <div className="manga-panel">
                                        {teamAuditEntries.length > 0 ? (
                                            <div className="manga-audit-list">
                                                {teamAuditEntries.map((entry) => {
                                                    const createdAt = entry?.createdAt || entry?.created_at;
                                                    const date = createdAt ? new Date(createdAt).toLocaleString() : '';
                                                    const rollbackTargets = extractAuditTargets(entry);
                                                    const canRollback = isRollbackableAudit(entry)
                                                        && (rollbackTargets.mangaId || rollbackTargets.chapterId);
                                                    const rollbackLabel = rollbackTargets.mangaId
                                                        ? 'Восстановить тайтл'
                                                        : 'Восстановить главу';
                                                    return (
                                                        <div key={entry?.id || `${entry?.action}-${createdAt}`} className="manga-audit-item">
                                                            <div className="manga-audit-title">{entry?.action || 'Событие'}</div>
                                                            {entry?.actorId ? (
                                                                <div className="manga-audit-meta">Actor: {entry.actorId}</div>
                                                            ) : null}
                                                            {date ? <div className="manga-audit-meta">{date}</div> : null}
                                                            {entry?.details ? (
                                                                <div className="manga-audit-details">
                                                                    {typeof entry.details === 'string' ? entry.details : JSON.stringify(entry.details)}
                                                                </div>
                                                            ) : null}
                                                            {canRollback ? (
                                                                <div className="manga-studio-toolbar">
                                                                    <button
                                                                        type="button"
                                                                        className="ui-btn ui-btn--secondary ui-btn--sm"
                                                                        onClick={() => handleAuditRollback(entry, selectedTeamId)}
                                                                        disabled={teamAuditActionBusy}
                                                                    >
                                                                        {teamAuditActionBusy ? 'Восстановление...' : rollbackLabel}
                                                                    </button>
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div className="manga-empty">Аудит пока пуст.</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="manga-empty">Команда не выбрана.</div>
                        )}
                    </div>
                </div>
            ) : null}

            <MangaImageLightbox src={lightboxImage} alt={lightboxTitle} onClose={closeLightbox} />
        </AdminLayout>
    );
};

export default MangaModerationPage;
